import { Injectable, signal } from '@angular/core';

/**
 * Tracks the number of in-flight API requests and exposes a single `visible`
 * signal that drives the global loading spinner. A short delay avoids flicker
 * on fast requests, and the counter guarantees the spinner clears once every
 * request has settled (see HttpLoadingInterceptor's `finalize`).
 */
@Injectable({ providedIn: 'root' })
export class LoadingIndicatorService {
  private _visible = signal<boolean>(false);
  private _activeRequests = 0;
  private _timer: ReturnType<typeof setTimeout> | null = null;
  private readonly _delayMs = 300;

  visible = this._visible.asReadonly();

  increment(): void {
    this._activeRequests++;

    // Only start the delay timer on the first concurrent request.
    if (this._activeRequests === 1 && !this._timer) {
      this._timer = setTimeout(() => {
        this._timer = null;
        if (this._activeRequests > 0) {
          this._visible.set(true);
        }
      }, this._delayMs);
    }
  }

  decrement(): void {
    this._activeRequests = Math.max(0, this._activeRequests - 1);

    if (this._activeRequests === 0) {
      if (this._timer) {
        clearTimeout(this._timer);
        this._timer = null;
      }
      this._visible.set(false);
    }
  }

  reset(): void {
    this._activeRequests = 0;
    if (this._timer) {
      clearTimeout(this._timer);
      this._timer = null;
    }
    this._visible.set(false);
  }
}
